import { useEffect } from 'react';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';
import { useStepperActions } from '../../core/stepper/useStepperActions';
import {
    usePortfolioProcessLayoutActions,
} from '../../datasource/PortfolioProcessLayout/usePortfolioProcessLayoutActions';
import { useCreatePortfolioActions, useCreatePortfolioState } from '../../datasource/CreatePortfolio';

type SaveOnNavigationProps = {
    saveOnContinue?: () => any;
    saveOnBack?: () => any;
    enableAutoStepComplete?: boolean;
    enableAutoStepBack?: boolean;
    pageErrors?: TPageError;
    continueInProgress?: boolean;
    continueDisabled?: boolean;
    navigationDisabled?: boolean;
    backDisabled?: boolean;
    preventNavigationLock?: boolean;
    hideStepperButtons?: boolean;
}

export const usePortfolioProcessNavigation = ({
    saveOnContinue,
    saveOnBack,
    enableAutoStepComplete = true,
    enableAutoStepBack = true,
    pageErrors = undefined,
    navigationDisabled = false,
    continueInProgress = false,
    continueDisabled = false,
    backDisabled = false,
    preventNavigationLock = false,
    hideStepperButtons = false,
}: SaveOnNavigationProps = {}) => {
    const {
        stepComplete, stepBack, readyToNavigateAction, setStep,
    } = useStepperActions();
    const {
        onContinueAction, onBackAction, continueLoadingAction,
        continueDisabledAction, backDisabledAction, hideStepperButtonsAction,
        onPageErrorAction,
    } = usePortfolioProcessLayoutActions();
    const {
        readyToNavigate,
    } = useCreatePortfolioState();

    const {
        lockNavigationAction,
        unlockNavigationAction,
    } = useCreatePortfolioActions();

    useEffect(() => {
        if (!preventNavigationLock) {
            lockNavigationAction();
            readyToNavigateAction(false);
            stepComplete(false);
        }
    }, [preventNavigationLock]);

    useEffect(() => {
        onPageErrorAction(undefined);
    }, []);

    useEffect(() => {
        readyToNavigateAction(!!readyToNavigate);
    }, [readyToNavigate, readyToNavigateAction]);

    useEffect(() => {
        continueLoadingAction(continueInProgress);
    }, [continueInProgress]);

    useEffect(() => {
        onPageErrorAction(pageErrors);
    }, [pageErrors]);

    useEffect(() => {
        backDisabledAction(navigationDisabled || backDisabled);
        continueDisabledAction(navigationDisabled || continueDisabled);
    }, [navigationDisabled, continueDisabled, backDisabled]);

    useEffect(() => {
        hideStepperButtonsAction(hideStepperButtons);
    }, [hideStepperButtons]);

    useEffect(() => {
        onContinueAction(async () => {
            if (saveOnContinue) {
                await saveOnContinue();
                unlockNavigationAction();
            } else unlockNavigationAction();
            if (enableAutoStepComplete) stepComplete(true);
        });
    }, [stepComplete, saveOnContinue, enableAutoStepComplete, onContinueAction]);

    useEffect(() => {
        onBackAction(async () => {
            if (saveOnBack) await saveOnBack(); else unlockNavigationAction();
            if (enableAutoStepBack) stepBack();
        });
    }, [stepBack, saveOnBack, enableAutoStepBack]);

    return {
        stepComplete,
        unlockNavigationAction,
        setStep,
        hideStepperButtonsAction,
    };
};
